import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import AdminLayout from '../../components/AdminLayout';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { toast } from 'sonner';
import {
  ArrowLeft,
  Download,
  Filter,
  CheckCircle,
  XCircle,
  AlertCircle,
  RefreshCw
} from 'lucide-react';

const ScanLogs = () => {
  const { eventId } = useParams();
  const navigate = useNavigate();
  const [logs, setLogs] = useState([]);
  const [dashboards, setDashboards] = useState([]);
  const [event, setEvent] = useState(null);
  const [loading, setLoading] = useState(true);
  
  const [dashboardFilter, setDashboardFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dateFrom, setDateFrom] = useState('');
  const [dateTo, setDateTo] = useState('');
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [total, setTotal] = useState(0);

  const fetchInitialData = useCallback(async () => {
    try {
      const [eventRes, dashboardsRes] = await Promise.all([
        api.get(`/events/${eventId}`),
        api.get(`/events/${eventId}/dashboards`)
      ]);
      setEvent(eventRes.data);
      setDashboards(dashboardsRes.data || []);
    } catch (error) {
      toast.error('Failed to load event data');
      navigate('/admin/events');
    }
  }, [eventId, navigate]);

  const fetchLogs = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit: 100 };
      if (dashboardFilter !== 'all') params.dashboard_id = dashboardFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get(`/events/${eventId}/scan-logs`, { params });
      setLogs(response.data.logs || []);
      setTotalPages(response.data.pages || 1);
      setTotal(response.data.total || 0);
    } catch (error) {
      toast.error('Failed to load scan logs');
    } finally {
      setLoading(false);
    }
  }, [eventId, page, dashboardFilter, statusFilter, dateFrom, dateTo]);

  useEffect(() => {
    fetchInitialData();
  }, [fetchInitialData]);

  useEffect(() => {
    fetchLogs();
  }, [fetchLogs]);

  const handleExport = async () => {
    try {
      toast.info('Generating export...');
      const params = {};
      if (dashboardFilter !== 'all') params.dashboard_id = dashboardFilter;
      if (statusFilter !== 'all') params.status = statusFilter;
      if (dateFrom) params.date_from = dateFrom;
      if (dateTo) params.date_to = dateTo;
      
      const response = await api.get(`/events/${eventId}/scan-logs/export`, {
        params,
        responseType: 'blob'
      });
      
      // Get filename from Content-Disposition header or generate default
      const contentDisposition = response.headers['content-disposition'];
      let filename = 'scan_logs.xlsx';
      if (contentDisposition) {
        const filenameMatch = contentDisposition.match(/filename=(.+)/);
        if (filenameMatch) {
          filename = filenameMatch[1].replace(/"/g, '');
        }
      }
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      toast.success('Export downloaded!');
    } catch (error) {
      toast.error('Failed to export logs');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'success':
        return <CheckCircle className="h-4 w-4 text-green-500" />;
      case 'duplicate':
        return <AlertCircle className="h-4 w-4 text-yellow-500" />;
      case 'invalid':
      case 'unauthorized':
        return <XCircle className="h-4 w-4 text-red-500" />;
      default:
        return null;
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      success: 'bg-green-100 text-green-700',
      duplicate: 'bg-yellow-100 text-yellow-700',
      invalid: 'bg-red-100 text-red-700',
      unauthorized: 'bg-orange-100 text-orange-700'
    };
    return (
      <span className={`px-2 py-1 rounded-full text-xs ${styles[status] || 'bg-gray-100 text-gray-700'}`}>
        {status}
      </span>
    );
  };

  return (
    <AdminLayout>
      {/* Header */}
      <div className="flex items-center gap-4 mb-6">
        <Button variant="ghost" size="icon" onClick={() => navigate(`/admin/events/${eventId}`)}>
          <ArrowLeft className="h-5 w-5" />
        </Button>
        <div className="flex-1">
          <h1 className="text-2xl font-bold">Scan Logs</h1>
          <p className="text-gray-500">{event?.event_name} • {total} total scans</p>
        </div>
        <Button onClick={handleExport} variant="outline">
          <Download className="h-4 w-4 mr-2" /> Export Excel
        </Button>
      </div>

      {/* Filters */}
      <Card className="mb-6">
        <CardContent className="p-4">
          <div className="flex flex-wrap gap-4 items-end">
            <div className="space-y-1">
              <label className="text-sm text-gray-500">Scanner</label>
              <Select value={dashboardFilter} onValueChange={(v) => { setDashboardFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[180px]">
                  <SelectValue placeholder="All Scanners" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Scanners</SelectItem>
                  {dashboards.map((d) => (
                    <SelectItem key={d.dashboard_id} value={d.dashboard_id}>
                      {d.dashboard_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className="text-sm text-gray-500">Status</label>
              <Select value={statusFilter} onValueChange={(v) => { setStatusFilter(v); setPage(1); }}>
                <SelectTrigger className="w-[150px]">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="duplicate">Duplicate</SelectItem>
                  <SelectItem value="invalid">Invalid</SelectItem>
                  <SelectItem value="unauthorized">Unauthorized</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-1">
              <label className="text-sm text-gray-500">From Date</label>
              <Input
                type="date"
                value={dateFrom}
                onChange={(e) => { setDateFrom(e.target.value); setPage(1); }}
                className="w-[150px]"
              />
            </div>
            
            <div className="space-y-1">
              <label className="text-sm text-gray-500">To Date</label>
              <Input
                type="date"
                value={dateTo}
                onChange={(e) => { setDateTo(e.target.value); setPage(1); }}
                className="w-[150px]"
              />
            </div>
            
            <Button variant="outline" onClick={() => {
              setDashboardFilter('all');
              setStatusFilter('all');
              setDateFrom('');
              setDateTo('');
              setPage(1);
            }}>
              <RefreshCw className="h-4 w-4 mr-2" /> Reset
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Logs Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex justify-center py-12">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
            </div>
          ) : logs.length === 0 ? (
            <div className="text-center py-12 text-gray-500">
              No scan logs found for the selected filters
            </div>
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Time</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Attendee</TableHead>
                    <TableHead>Ticket ID</TableHead>
                    <TableHead>Scanner</TableHead>
                    <TableHead>Type</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {logs.map((log) => (
                    <TableRow key={log.scan_id}>
                      <TableCell className="whitespace-nowrap">
                        {log.scanned_at?.slice(0, 19).replace('T', ' ')}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-2">
                          {getStatusIcon(log.scan_status)}
                          {getStatusBadge(log.scan_status)}
                        </div>
                      </TableCell>
                      <TableCell>{log.attendee_name || '-'}</TableCell>
                      <TableCell className="font-mono text-sm">{log.ticket_id || '-'}</TableCell>
                      <TableCell>{log.dashboard_name || log.scanner_username}</TableCell>
                      <TableCell>
                        <span className="text-sm text-gray-500 capitalize">
                          {log.dashboard_type || '-'}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
              
              {/* Pagination */}
              {totalPages > 1 && (
                <div className="flex items-center justify-between p-4 border-t">
                  <p className="text-sm text-gray-500">Page {page} of {totalPages}</p>
                  <div className="flex gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.max(1, p - 1))}
                      disabled={page === 1}
                    >
                      Previous
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => setPage(p => Math.min(totalPages, p + 1))}
                      disabled={page === totalPages}
                    >
                      Next
                    </Button>
                  </div>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </AdminLayout>
  );
};

export default ScanLogs;
